import { useEffect, useMemo, FC, useState } from "react";
import "./SideBar.scss";
import IceCreamLogo from "assets/side-bar/ice-cream.svg"
import ProductLogo from "assets/side-bar/product.svg"
import ForcastLogo from "assets/side-bar/forcast.svg"
import DashboardLogo from "assets/side-bar/dashboard.svg"
import AmountLogo from "assets/side-bar/amount.svg"
import SelectedShopLogo from "assets/side-bar/selected-shop.svg"
import SettingsLogo from "assets/side-bar/settings.svg"
import UserLogo from "assets/side-bar/user.svg"
import ShopLogo from "assets/side-bar/shop.svg"
import HistoryLogo from "assets/side-bar/history.svg"
import { Dropdown } from 'primereact/dropdown';
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AppRoutes } from "features/authentication/types/app-routes";
import { v4 as uuidv4 } from 'uuid';
import { setSelectedShop } from "features/authentication/userSlices";
import { PlanSelectorEnum } from "features/profile/components/shop-management/shop-management";
import { Tooltip } from 'primereact/tooltip';

export enum UserRoles  {
  EMPLOYEE = 1,
  MANAGER= 2,
  SUPER_MANAGER= 3,
  QOLAIG= 4
}

const SideBar = () => {
  const dispatch: Function = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.auth.user);
  const location = useLocation();
  const [selectedShop, setLocalSelectedShop] = useState(null);
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const shopIdFromUrl = searchParams.get('shopId');
    if (shopIdFromUrl) {
      const shopFromUrl = user.shops.find(shop => shop.id === shopIdFromUrl);
      if (shopFromUrl !== selectedShop) {
        setLocalSelectedShop(shopFromUrl);
        dispatch(setSelectedShop(shopFromUrl.id));
      }
    } else if (user.shops[0]) {

      const defaultShop = user.shops[0];
      setLocalSelectedShop(defaultShop);
      dispatch(setSelectedShop(defaultShop.id));
      
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('shopId', defaultShop.id);
      navigate({
        pathname: location.pathname,
        search: newSearchParams.toString()
      }, { replace: true }); 
    }
  }, []);

  const handleSelectedShop = (shop) => {
    setLocalSelectedShop(shop);
    dispatch(setSelectedShop(shop.id));
    
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('shopId', shop.id);
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString()
    });
  };

  useEffect(() => {
    const shopIdFromUrl = searchParams.get('shopId');
    if (shopIdFromUrl) {
      const shopFromUrl = user.shops.find(shop => shop.id === shopIdFromUrl);
      if (shopFromUrl) {
        setLocalSelectedShop(shopFromUrl);
        dispatch(setSelectedShop(shopFromUrl.id));
      }
    }
  }, []);

  const shopTemplate = (option) => {
    if (!option) return null;
    return (
      <div className="shop" key={uuidv4()}>
        <img alt={option.name} src={SelectedShopLogo} />
        <div className="shop__details">
          <div className="shop__details__name">{option.name}</div>
          <div className="shop__details__address">{option.address}</div>
        </div>
      </div>
    );
  };
const getNavigationPath = (basePath) => {
  const newSearchParams = new URLSearchParams(searchParams);
  if (selectedShop?.id) {
    newSearchParams.set('shopId', selectedShop.id);
  }
  return `${basePath}?${newSearchParams.toString()}`;
};
  const sections = useMemo(() => {
    return [
      {
        text: 'GENERAL',
        minRoleLevel: UserRoles.MANAGER,
        modules: [
          {
            text: 'Dashboard',
            minRoleLevel: UserRoles.MANAGER,
            icon: DashboardLogo,
            selectedIcon: DashboardLogo,
            path: AppRoutes.DASHBOARD,
            disabled: selectedShop?.plan === PlanSelectorEnum.STANDARD,
            handleClick:selectedShop?.plan === PlanSelectorEnum.PRO ? () => navigate(getNavigationPath(`${AppRoutes.DASHBOARD}`)) : null
          }
        ]
      },
      {
        text: 'STOCK MANAGEMENT',
        minRoleLevel: UserRoles.EMPLOYEE,
        modules: [
          {
            text: 'Ice cream',
            minRoleLevel: UserRoles.EMPLOYEE,
            icon: IceCreamLogo,
            selectedIcon: IceCreamLogo,
            path: `${AppRoutes.STOCK_MANAGEMENT}/ice-cream`,
            handleClick: () => navigate(getNavigationPath(`${AppRoutes.STOCK_MANAGEMENT}/ice-cream`)) // Keep query params
          },
          {
            text:'Other products',
            minRoleLevel: UserRoles.EMPLOYEE,
            icon: AmountLogo,
            selectedIcon: AmountLogo,
            path: `${AppRoutes.STOCK_MANAGEMENT}/other-products`,
            handleClick: () => navigate(getNavigationPath(`${AppRoutes.STOCK_MANAGEMENT}/other-products`)), // Keep query params
          }
        ]
      },
      {
        text: 'STOCK',
        minRoleLevel: UserRoles.MANAGER,
        modules: [
          {
            text: 'Amount available per product',
            minRoleLevel: UserRoles.MANAGER,
            icon: IceCreamLogo,
            selectedIcon: IceCreamLogo,
            path: `${AppRoutes.STOCK}`,
            handleClick: () => navigate(getNavigationPath(`${AppRoutes.STOCK}`)) // Keep query params
          }
        ]
      },
      {
        text: 'CONSUMPTION',
        minRoleLevel: UserRoles.MANAGER,
        modules: [
          {
            text: 'Per product',
            minRoleLevel: UserRoles.MANAGER,
            icon: ProductLogo,
            selectedIcon: ProductLogo,
            path: `${AppRoutes.CONSUMPTION}/product`,
            handleClick: () => navigate(getNavigationPath(`${AppRoutes.CONSUMPTION}/product`)) // Keep query params
          },
          {
            text:'Forecast',
            minRoleLevel: UserRoles.MANAGER,
            icon: ForcastLogo,
            selectedIcon: ForcastLogo,
            path: `${AppRoutes.CONSUMPTION}/forecast`,
            disabled: selectedShop?.plan === PlanSelectorEnum.STANDARD,
            handleClick:selectedShop?.plan === PlanSelectorEnum.PRO ? () => navigate(getNavigationPath(`${AppRoutes.CONSUMPTION}/forecast`)) : null, // Keep query params
          }
        ]
      },
      {
        text: 'OTHER',
        minRoleLevel: UserRoles.EMPLOYEE,
        modules: [
          {
            text: 'User management',
            minRoleLevel: UserRoles.SUPER_MANAGER,
            icon: UserLogo,
            selectedIcon: UserLogo,
            path: AppRoutes.USER_MANAGEMENT,
            handleClick: () => navigate(getNavigationPath(`${AppRoutes.USER_MANAGEMENT}`)), // Keep query params
          },
          {
            text: 'Shop management',
            minRoleLevel: UserRoles.SUPER_MANAGER,
            icon: ShopLogo,
            selectedIcon: ShopLogo,
            path: AppRoutes.SHOP_MANAGEMENT,
            handleClick: () => navigate(getNavigationPath(`${AppRoutes.SHOP_MANAGEMENT}`)) // Keep query params
          },
          {
            text: 'Activity log',
            minRoleLevel: UserRoles.SUPER_MANAGER,
            icon: HistoryLogo,
            selectedIcon: HistoryLogo,
            path: AppRoutes.ACTIVITY_LOG,
            handleClick: () => navigate(getNavigationPath(`${AppRoutes.ACTIVITY_LOG}`)) // Keep query params
          },
          {
            text:'Settings',
            minRoleLevel: UserRoles.EMPLOYEE,
            icon: SettingsLogo,
            selectedIcon: SettingsLogo,
            path: AppRoutes.SETTINGS,
            handleClick: () => navigate(getNavigationPath(`${AppRoutes.SETTINGS}`)), // Keep query params
          }
        ]
      }
    ];
  }, [location.search,user.role.level, selectedShop?.plan]); 

  const createSection = (section) => {
    if (user.roleLevel < section.minRoleLevel) {
      return null;
    }
    return (
      <div className="nav-bar__top__sections__section" key={uuidv4()}>
        <div className="nav-bar__top__sections__section__label">{section.text}</div>
        {section.modules.map((module) => {
          const isSelected = location.pathname === module.path;
          if (user.roleLevel < module.minRoleLevel) {
            return null;
          }
          return (
            <div key={module.path}>
              {module.disabled && (
                <Tooltip
                  target={`.tooltip-${module.text.replace(/\s+/g, '-')}`}
                  content="Pro version required to access this feature"
                  position="right"
                />
              )}
              <div
                className={`nav-bar__top__sections__module ${isSelected ? 'selected' : ''} ${
                  module.disabled ? 'disabled tooltip-' + module.text.replace(/\s+/g, '-') : ''
                }`}
                onClick={module.handleClick}
              >
                <img
                  className="nav-bar__top__sections__module__icon"
                  src={isSelected ? module.selectedIcon : module.icon}
                  alt={module.text}
                />
                <div className="nav-bar__top__sections__module__text">{module.text}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  const shopDropdown = useMemo(() => {
    return (<Dropdown value={selectedShop} onChange={(e) => handleSelectedShop(e.value)} options={user.shops} optionLabel="name" placeholder="Select a shop" 
    valueTemplate={shopTemplate} itemTemplate={shopTemplate} className="shop-dropdown"
/> )
  }, [selectedShop])
  return (
    <div className="nav-bar">
      <div className="nav-bar__top">
       {/* { user.roleLevel == UserRoles.EMPLOYEE &&  */}
        {shopDropdown}        
        <div className="nav-bar__top__sections">
          {sections.map((section) => (
            createSection(section)
          ))}
      </div>
      </div>
      <div className="nav-bar__footer">
          <div className="nav-bar__footer__text">{'@ 2024 Qolaig'}</div>
      </div>
    </div>
  );
};

export default SideBar;
